import { Box, Card, Chip, CircularProgress } from "@mui/material";
import FaiBox from "MuiComponents/FaiBox";
import FaiButton from "MuiComponents/FaiButton";
import FaiTypography from "MuiComponents/FaiTypography";
import appStore from "store/general";
import CouponDetails from "./CouponDetails";
import NoCoupons from "./NoCoupons";
import { useEffect, useMemo, useState } from "react";
import PageHeader from "screens/OnBoarding/components/PageHeader";
import SearchInput from "components/SearchInput";
import FaiDataGrid from "components/DataGrid/FaiDataGrid";
import { GridPaginationModel } from "@mui/x-data-grid";
import Axios from "services/api/config";
import { getAllCoupons } from "services/api/routes/Coupons";

type ChipColor = "default" | "success" | "error" | "warning" | "info";

const statusColor = (status?: string): ChipColor => {
	const s = (status || "").toUpperCase();
	if (s === "ACTIVE") return "success";
	if (s === "DELETED" || s === "EXPIRED" || s === "INACTIVE") return "error";
	if (s === "PENDING") return "warning";
	return "info";
};

const columns: any[] = [
	{ headerName: "Code", field: "couponCode" },
	{ headerName: "Campaign", field: "campaignName" },
	{ headerName: "Type", field: "couponType" },
	{ headerName: "Discount", field: "discount" },
	{ headerName: "Max. Discount", field: "maximum" },
	{ headerName: "Min. Order", field: "minimum" },
	{ headerName: "Categories", field: "categories" },
	{ headerName: "Valid From", field: "validFrom" },
	{ headerName: "Valid Till", field: "validTill" },
	{
		headerName: "Status",
		field: "status",
		renderCell: ({ row }: any) => (
			<Chip label={row.status || "-"} color={statusColor(row.status)} size="small" />
		),
	},
];

const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : "-");

// Flatten a backend Coupon into a flat row.
const toRow = (c: any) => {
	const isFlat = (c?.couponType || "").includes("FLAT");
	return {
		id: c?.id,
		couponCode: c?.couponCode || "-",
		campaignName: c?.campaignName || "-",
		couponType: c?.couponType || "-",
		discount: isFlat
			? `₹ ${c?.percentageFlat ?? 0}`
			: `${c?.percentageFlat ?? 0}%`,
		minimum: `₹ ${c?.minimumPrice ?? 0}`,
		maximum: c?.maximumPrice != null ? `₹ ${c.maximumPrice}` : "-",
		categories:
			(Array.isArray(c?.masterCategories) ? c.masterCategories : [])
				.map((m: any) => m?.name).filter(Boolean).join(", ") || "-",
		validFrom: fmtDate(c?.validFromDateTime),
		validTill: fmtDate(c?.validTillDateTime),
		status: c?.status || "-",
		raw: c,
	};
};

function CouponList() {
	const { couponData, setCouponData }: any = appStore();
	const [coupons, setCoupons] = useState<any[]>([]);
	const [loading, setLoading] = useState(false);
	const [rowCount, setRowCount] = useState(0);
	const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
		page: 0,
		pageSize: 10,
	});
	const [search, setSearch] = useState("");

	const fetchCoupons = async () => {
		try {
			setLoading(true);
			const query = `page=${paginationModel.page}&size=${paginationModel.pageSize}`;
			const { data } = await Axios({ ...getAllCoupons(query) });
			const responseData = data?.data || {};
			setCoupons((responseData.data || []).map(toRow));
			setRowCount(responseData.totalElements || 0);
		} catch (error) {
			console.error("Error fetching coupons:", error);
			setCoupons([]);
		} finally {
			setLoading(false);
		}
	};

	// Reload the list when not in the create/edit view (initial mount and after
	// returning from a successful create, which sets couponData back to falsy).
	useEffect(() => {
		if (!couponData) fetchCoupons();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [couponData, paginationModel.page, paginationModel.pageSize]);

	const visibleRows = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return coupons;
		return coupons.filter(
			(r) =>
				r.couponCode.toLowerCase().includes(q) ||
				r.campaignName.toLowerCase().includes(q)
		);
	}, [coupons, search]);

	const handlePaginationModelChange = (model: GridPaginationModel) => {
		setPaginationModel({ page: model.page, pageSize: model.pageSize });
	};

	if (couponData)
		return (
			<Card className="my-3 p-3 md:p-5">
				<CouponDetails />
			</Card>
		);

	return (
		<Card className="my-6 p-3 md:p-6">
			<FaiBox className="flex justify-between items-start gap-3">
				<PageHeader
					title="Coupons"
					content="Create and manage platform discount coupons to boost sales. Track each coupon and adjust offers to maximise impact."
				/>
				<FaiButton color="primary" onClick={() => setCouponData({})}>
					Add new Coupon
				</FaiButton>
			</FaiBox>

			{loading ? (
				<Box className="flex justify-center p-6">
					<CircularProgress />
				</Box>
			) : coupons.length === 0 ? (
				<NoCoupons onAddNew={() => setCouponData({})} />
			) : (
				<>
					<FaiBox className="!mt-4 mb-3">
						<SearchInput
							placeholder="Search by Code / Campaign"
							value={search}
							onChange={(e: any) => setSearch(e.target.value)}
						/>
					</FaiBox>
					<FaiDataGrid
						rows={visibleRows}
						columns={columns}
						pagination={true}
						pageSize={paginationModel.pageSize}
						rowCount={rowCount}
						paginationMode="server"
						onPaginationModelChange={handlePaginationModelChange}
					/>
				</>
			)}
		</Card>
	);
}

export default CouponList;
